import Controller, { inject as controller } from '@ember/controller';
import $ from 'jquery';
import { all, allSettled } from 'rsvp';
import { A } from '@ember/array';
import { readOnly } from '@ember/object/computed';
import { scheduleOnce } from '@ember/runloop';
import EmberObject, { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { showModal, closeModal } from 'sh-www/utils/modal-controls';
import AssetUploadManager from 'sh-www/mixins/asset-upload-manager';
import clsx from 'clsx';

const STANDARD_THREAD_PAGE_SIZE = 5;
const SEARCH_THREAD_PAGE_SIZE = 10;

class MessageCenterController extends Controller.extend(AssetUploadManager) {
  @controller application;

  @service('customEndpointWrapper') CEW;
  @service('error-tracker') errorTracker;
  @service('managers/threadManager') threadManager;
  @service('sortingUtils') sortingUtils;
  @service('state-manager-hybrids/recent-threads-state-manager-hybrid') recentThreadsHybrid;
  @service('state-manager-hybrids/heartbeat-state-manager-hybrid') heartbeatStateManager;
  @service router;
  @service session;
  @service shData;
  @service store;
  @service api;
  @service('states/message-center') mcStates;

  @readOnly('session.currentUser') currentUser;

  composerHasFocus = false;

  @action
  setComposerHasFocus(hasFocus) {
    this.set('composerHasFocus', hasFocus);
  }

  @action
  preventFormSubmission(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  }

  drawerOpen = false;

  @action
  setCurrentComposedMessageModel(value) {
    this.set('mcStates.currentComposedMessageModel', value);
  }

  composerElement = null;

  @action
  setComposerElement(value) {
    this.set('composerElement', value);
  }

  @action
  closeDrawer() {
    this.set('drawerOpen', false);
  }

  @action
  removeAssets() {
    this.resetAssetUploadManager();
  }

  @computed(
    'showThreadBreakdown',
    'showBeginConversation',
    'isLoadingRequiredResources',
    'emptyThreadList',
    'showInboxView',
    'showArchiveView',
    'drawerOpen',
  )
  get bodyClasses() {
    return clsx(
      'message-center',
      this.get('showThreadBreakdown') && 'show-thread-breakdown',
      this.get('showBeginConversation') && 'show-begin-conversation',
      !this.get('isLoadingRequiredResources') && 'page-setup-loaded',
      this.get('emptyThreadList') && 'thread-list-empty',
      this.get('showInboxView') && 'show-inbox-view',
      this.get('showArchiveView') && 'show-archive-view',
      this.get('drawerOpen') && 'drawer-open',
    );
  }

  @computed(
    'showExpandedPanel',
    'showInboxView',
    'showArchiveView',
    'showBeginConversation',
    'showThreadBreakdownDelayedUpdate',
    'emptyThreadList',
  )
  get threePanelContainerClasses() {
    return clsx(
      'three-panel-container panel-container',
      this.get('showExpandedPanel') && 'expanded-panel-enabled',
      this.get('showInboxView') && 'inbox-open',
      this.get('showArchiveView') && 'archive-open',
      this.get('showBeginConversation') && 'begin-conversation-open',
      this.get('showThreadBreakdownDelayedUpdate') && 'show-thread-breakdown',
      this.get('emptyThreadList') && 'thread-list-empty',
    );
  }

  @computed('drawerOpen')
  get panelThreeClasses() {
    return clsx(
      'panel panel-3 drawer',
      this.get('drawerOpen') && 'active-drawer',
    );
  }

  @computed(
    'composerHasFocus',
    '_isMessageBlocked',
    'currentComposedMessageModel',
  )
  get composerContainerClasses() {
    return clsx(
      'thread-breakdown panel-content-container',
      this.get('_isMessageBlocked') && 'message-blocked',
      this.get('composerHasFocus') && 'composer-active',
      this.get('currentComposedMessageModel')?.trim() && 'composer-has-text',
    );
  }

  //*******************************
  /////////////////////////////////
  // Modal Configs
  /////////////////////////////////
  //*******************************


  modals = {
    manageWatchers: {
      threadToWatch: undefined,
      colleaguesList: undefined,
    },
    declineOnArchive: {
      currentThread: undefined,
      pendingProjects: undefined,
      afterSuccessfulDeclineCallback: () => {},
    }
  };

  //*******************************
  /////////////////////////////////
  // Routing
  /////////////////////////////////
  //*******************************


  /////////////////////////////////
  // routeSegment
  //
  // * routeSegment tracks the URI and updates on transitions within Message Center
  // * nested routes.
  /////////////////////////////////

  routeSegment = {
    view: undefined,
    threadId: undefined
  };

  /** The time of the most recent routeSegment change. */
  lastRouteChangeTimestamp = null;

  /** A cache of the previous ID for use in the routeSegment observer. */
  previousThreadID = null;

  @action
  watchRouteSegment() {
    const previousThreadID = this.get('previousThreadID');
    const lastRouteChangeTimestamp = this.get('lastRouteChangeTimestamp');
    const threadId = this.get('routeSegment.threadId');
    const loadingInitialized = typeof this.get('isLoadingRequiredResources') !== 'undefined';

    if (threadId === null && !loadingInitialized) {
      this._pageSetup();
    } else if (threadId !== null && !loadingInitialized) {
      this._setThreadModelToRouteId();
      this._pageSetup();
    } else if (threadId) {
      if (previousThreadID) {
        // Mark the previous thread read up to the last message sent before opening the thread.
        this._markThreadRead({ threadID: previousThreadID, timestamp: lastRouteChangeTimestamp });
      }

      this._setThreadModelToRouteId();
    } else {
      if (previousThreadID) {
        // Mark the previous thread read up to the last message sent before opening the thread.
        this._markThreadRead({ threadID: previousThreadID, timestamp: lastRouteChangeTimestamp });
      }

      this.set('currentThreadModel', undefined);
    }

    // Store data about this route segment change for next time.
    this.set('previousThreadID', threadId);
    this.set('lastRouteChangeTimestamp', new Date());
  }

  @computed('currentUser.isSeller', '_buyer', 'currentThreadModel.assignment')
  get showCalculator() {
    return this.currentUser.isSeller
      && this._buyer?.isBuyer
      && this._buyer?.company
      && this.currentThreadModel.assignment;
  }


  //*******************************
  /////////////////////////////////
  // View/State-Change
  /////////////////////////////////
  //*******************************


  /////////////////////////////////
  // Sub Navigation
  //
  // * Governs the sub navigation in Message Center
  /////////////////////////////////

  subNavModel = {
    inbox: {
      text: 'Inbox',
      href: '#inbox',
      routeName: 'message-center.inbox',
      unreadCount: 0
    },
    archive: {
      text: 'Archive',
      href: '#archive',
      routeName: 'message-center.archive',
      unreadCount: 0
    }
  };

  @action
  inboxUnreadCount() {
    let count = this.get('heartbeatStateManager.threadsUnreadInbox');

    this.set('subNavModel.inbox.unreadCount', count);
  }

  @action
  archiveUnreadCount() {
    let count = this.get('heartbeatStateManager.threadsUnreadArchive');

    this.set('subNavModel.archive.unreadCount', count);
  }

  /////////////////////////////////
  // Loading States
  //
  // * A collection of loading states and required resource tracking
  /////////////////////////////////

  isLoadingThreads = {
    inbox: undefined,
    archive: undefined
  };

  threadsRequiredComplete = {
    inbox: undefined,
    archive: undefined
  };

  isLoadingColleaguesList = undefined;
  colleaguesListRequiredComplete = undefined;

  isLoadingThreadSearchResults = undefined;
  isLoadingGetMoreSearchThreadsResults = undefined;

  /////////////////////////////////
  // Polling/Pulling meta tracking
  //
  // * Meta information for thread + messages polling
  /////////////////////////////////


  lastThreadPage = {
    inbox: undefined,
    archive: undefined,
    search: undefined
  };

  maxThreadPages = {
    inbox: undefined,
    archive: undefined,
    search: undefined
  };

  totalThreadResults = {
    inbox: undefined,
    archive: undefined,
    search: undefined
  };

  threadSnapshotDates = {
    inbox: undefined,
    archive: undefined,
    search: undefined,
    pollingThreads: undefined,
    pollingMessages: undefined
  };


  /////////////////////////////////
  // Thread search meta tracking
  //
  // * Meta information for thread searches
  /////////////////////////////////

  threadSearchLastQuery = undefined;

  /////////////////////////////////
  // Page Setup Completion
  //
  // * Determines when the minimum requirements to show page elements have been satisfied
  /////////////////////////////////

  @computed('allThreadsModel', 'threadListModel', 'isLoadingRequiredResources')
  get pageSetupComplete() {
    let threadListModel = this.get('threadListModel');
    let isLoadingRequiredResources = this.get('isLoadingRequiredResources');

    // Threads condition
    return ((typeof threadListModel !== 'undefined') && !isLoadingRequiredResources);
  }


  /////////////////////////////////
  // Inbox vs Archive
  //
  // * Determines which view to show
  /////////////////////////////////


  @computed('routeSegment.view')
  get showInboxView() {
    let view = this.get('routeSegment.view');

    scheduleOnce('afterRender', this, function () {
      let mcStates = this.get('mcStates');
      mcStates.set('subNav.isTransitioning', false);
    });

    return (view === 'inbox');
  }

  @computed('showInboxView')
  get showArchiveView() {
    return !this.showInboxView;
  }


  /////////////////////////////////
  // noThreadsNoContacts (zero-conditions)
  //
  // * Determines when a user has neither any threads nor any contacts
  /////////////////////////////////

  @computed(
    'allThreadsModel.[]',
    'threadsRequiredComplete.inbox',
    'threadsRequiredComplete.archive',
  )
  get noThreadsNoContacts() {
    let allThreadsModel = this.get('allThreadsModel');

    let cond1 = !allThreadsModel.get('length');
    let cond2 = this.get('threadsRequiredComplete.inbox') && this.get('threadsRequiredComplete.archive');

    return cond1 && cond2;
  }


  /////////////////////////////////
  // showBeginConversation
  //
  // * Show the "create a thread" experience
  /////////////////////////////////

  showBeginConversation = undefined;

  /////////////////////////////////
  // showExpandedPanel
  //
  // * Determines when the first panel should expand to the full width of the screen
  /////////////////////////////////

  @computed('currentThreadModel', 'threadListModel.[]', 'showBeginConversation', 'mcStates.subNav.isTransitioning')
  get showExpandedPanel() {
    let currentThreadModel = this.get('currentThreadModel');
    let showBeginConversation = this.get('showBeginConversation');

    let showInboxView = this.get('showInboxView');
    let isTransitioning = this.get('mcStates.subNav.isTransitioning');

    let emptyState = this.get('emptyThreadList');

    let cond1 = showInboxView && !showBeginConversation && !currentThreadModel;
    let cond2 = !showInboxView && (emptyState || !currentThreadModel);
    let cond3 = isTransitioning;

    return (cond1 || cond2 || cond3);
  }

  /////////////////////////////////
  // emptyThreadList
  //
  // * Indicates that the list for the current view has both been initialized and is empty
  /////////////////////////////////

  @computed('threadListModel.[]')
  get emptyThreadList() {
    let threadListModel = this.get('threadListModel');

    let emptyState = (typeof threadListModel !== 'undefined') && !threadListModel.get('length');

    return emptyState;
  }

  /////////////////////////////////
  // showThreadBreakdown
  //
  // * Determines whether to show the message breakdown of a thread
  /////////////////////////////////

  @computed('currentThreadModel', 'showBeginConversation')
  get showThreadBreakdown() {
    return (!this.get('showBeginConversation') && !!this.get('currentThreadModel'));
  }

  // Lags the thread breakdown until after Ember's run loop has completed
  showThreadBreakdownDelayedUpdate = undefined;

  @action
  watchShowThreadBreakdown() {
    let showThreadBreakdown = this.get('showThreadBreakdown');

    scheduleOnce('afterRender', this, function () {
      this.set('showThreadBreakdownDelayedUpdate', showThreadBreakdown);
    });
  }

  /////////////////////////////////
  // showGetMoreSearchThreadsButton
  //
  // * Determines whether to show the "get more" button
  // * at the bottom of the search results list
  /////////////////////////////////

  @computed('lastThreadPage.search', 'maxThreadPages.search')
  get showGetMoreSearchThreadsButton() {
    let lastPage = this.get('lastThreadPage.search');
    let maxPages = this.get('maxThreadPages.search');

    return (lastPage < maxPages);
  }

  @computed('threadSearchResultsModel')
  get showThreadSearchResults() {
    return typeof this.get('threadSearchResultsModel') !== 'undefined';
  }


  /////////////////////////////////
  // threadMenuIsBusy
  //
  // * Determines whether to show the "get more" button
  // * at the bottom of the search results list
  /////////////////////////////////

  @computed(
    'mcStates.threadBreakdown.markUnreadAwaitingResponse',
    'mcStates.threadBreakdown.archiveConversationAwaitingResponse',
    'mcStates.threadBreakdown.restoreConversationAwaitingResponse',
  )
  get threadMenuIsBusy() {
    let markUnread = this.get('mcStates.threadBreakdown.markUnreadAwaitingResponse');
    let archive = this.get('mcStates.threadBreakdown.archiveConversationAwaitingResponse');
    let restore = this.get('mcStates.threadBreakdown.restoreConversationAwaitingResponse');

    return (markUnread || archive || restore);
  }


  //*******************************
  /////////////////////////////////
  // Model Management
  /////////////////////////////////
  //*******************************


  /////////////////////////////////
  // allThreadsModel
  //
  // * Collection of all threads loaded for the currentUser
  /////////////////////////////////

  allThreadsModel = undefined;

  @action
  watchAllThreadsModel() {
    let allThreads = this.get('allThreadsModel');
    let inboxThreads = A();
    let archivedThreads = A();

    if (allThreads && allThreads.length > 0) {

      allThreads.forEach((item) => {

        //////////////////////////////////////////////////
        // Build out the inbox + archive thread lists
        //////////////////////////////////////////////////

        if (!item.get('archivedAt')) {
          // Inbox Threads
          inboxThreads.pushObject(item);
        } else {
          // Archived Threads
          archivedThreads.pushObject(item);
        }

      });
    }

    this.setProperties({
      inboxThreadsModel: inboxThreads,
      archivedThreadsModel: archivedThreads
    });
  }

  /////////////////////////////////
  // polledThreadsModel
  //
  // * Collection of most recently retrieved set of polled threads
  /////////////////////////////////

  polledThreadsModel = undefined;

  @action
  watchPolledThreadsModel() {
    let polledThreadsModel = this.get('polledThreadsModel');
    let allThreadsModel = this.get('allThreadsModel').slice();
    allThreadsModel.addObjects(polledThreadsModel);
    this.set('allThreadsModel', allThreadsModel);
  }

  @readOnly('recentThreadsHybrid.mostRecentInboxThreadsGross') mostRecentInboxThreadsGrossModel;
  @readOnly('recentThreadsHybrid.mostRecentArchiveThreadsGross') mostRecentArchiveThreadsGrossModel;

  /////////////////////////////////
  //  Inbox/Archive Threads Models
  //
  // * The divided thread lists
  /////////////////////////////////

  inboxThreadsModel = undefined;
  archivedThreadsModel = undefined;

  /////////////////////////////////
  //  threadListModel
  //
  // * The list of threads for the current view. (Divided between inbox + archive)
  /////////////////////////////////

  @computed('showInboxView', 'inboxThreadsModel', 'archivedThreadsModel', 'inboxThreadsModel.[]', 'archivedThreadsModel.[]')
  get threadListModel() {
    let showInboxView = this.get('showInboxView');
    let inboxThreadsModel = this.get('inboxThreadsModel');
    let archivedThreadsModel = this.get('archivedThreadsModel');


    if (typeof inboxThreadsModel === 'undefined' || typeof archivedThreadsModel === 'undefined') {
      return;
    }

    if (showInboxView) {
      return this._sortThreadList(inboxThreadsModel);
    } else {
      return this._sortThreadList(archivedThreadsModel);
    }
  }

  /////////////////////////////////
  //  currentThreadModel
  //
  // * The thread that's currently being viewed/inspected by the user
  /////////////////////////////////

  currentThreadModel = undefined;
  currentThreadModelLastReadAt = undefined;

  @action
  watchCurrentThreadModel() {
    let currentPath = this.get('router.currentRouteName');
    let currentThreadModel = this.get('currentThreadModel');
    let store = this.get('store');

    // Reset the currentComposedMessage Model to clear the message composer
    this.setCurrentComposedMessageModel('');
    this.resetAssetUploadManager();

    if (currentThreadModel) {
      if (currentPath !== 'message-center.inbox.thread' || currentPath !== 'message-center.archive.thread') {
        if (this.get('showInboxView')) {
          this.get('router').transitionTo('message-center.inbox.thread', this.get('currentThreadModel.id'));
        } else {
          this.get('router').transitionTo('message-center.archive.thread', this.get('currentThreadModel.id'));
        }
      }
    } else {
      if (currentPath !== 'message-center.inbox.index' || currentPath !== 'message-center.archive.index') {
        if (this.get('showInboxView')) {
          this.get('router').transitionTo('message-center.inbox.index');
        } else {
          this.get('router').transitionTo('message-center.archive.index');
        }
      }
    }
  }

  @action
  watchCurrentThreadModelMessages() {
    let currentThreadModel = this.get('currentThreadModel');

    if (currentThreadModel) {
      let store = this.get('store');

      // Populate messages with their users
      let messages = this.get('currentThreadModel.messages');

      messages.forEach(message => {
        message.set('user', store.peekRecord('user', message.get('userId')));
      });
    }
  }

  /////////////////////////////////
  //  currentComposedMessageModel
  //
  // * The bound value to either of the message composers (used in both places)
  /////////////////////////////////

  @readOnly('mcStates.currentComposedMessageModel') currentComposedMessageModel;

  /////////////////////////////////
  //  currentThreadErrorInfo
  //
  // * An object that tracks composer errors when trying to send a message from an existing thread
  /////////////////////////////////

  @computed('currentThreadModel')
  get currentThreadErrorInfo() {
    return EmberObject.create({
      showWarning: false,
      warning: undefined,
      errorMessages: EmberObject.create({
        messageEmpty: 'You must compose a message'
      }),
      errors: A()
    });
  }

  @action
  currentThreadErrorWatcher() {
    let message = this.get('currentComposedMessageModel');
    let errors = this.get('currentThreadErrorInfo.errors');

    if (!message || message.length === 0) {
      errors.pushObject('messageEmpty');
    } else {
      errors.removeObject('messageEmpty');
    }
  }

  /////////////////////////////////
  //  newThreadModel
  //
  // * The thread model during new thread creation.
  // * Resets after thread has been persisted to server
  /////////////////////////////////

  newThreadModel = undefined;

  /////////////////////////////////
  //  newThreadErrorInfo
  //
  // * An object that tracks composer errors when trying to create a new thread
  /////////////////////////////////

  @computed('newThreadModel')
  get newThreadErrorInfo() {
    return EmberObject.create({
      showWarning: false,
      warning: undefined,
      errorMessages: EmberObject.create({
        threadTitleEmpty: 'Your message must have a title',
        recipientEmpty: 'You must select a recipient',
        messageEmpty: 'You must compose a message'
      }),
      errors: A()
    });
  }

  @action
  newThreadErrorWatcher() {
    let newThreadModel = this.get('newThreadModel');

    // Only check for errors if the newThreadModel exists.
    // (could potentially fire when undefined, due to currentComposedMessageModel)

    if (typeof newThreadModel === 'undefined') {
      return;
    }

    let threadTitle = this.get('newThreadModel.title');
    let recipient = this.get('newThreadModel').belongsTo('recipient');
    let message = this.get('currentComposedMessageModel');
    let errors = this.get('newThreadErrorInfo.errors');

    if (!threadTitle || threadTitle.length === 0) {
      errors.pushObject('threadTitleEmpty');
    } else {
      errors.removeObject('threadTitleEmpty');
    }

    if (!recipient || recipient.value() === null) {
      errors.pushObject('recipientEmpty');
    } else {
      errors.removeObject('recipientEmpty');
    }

    if (!message || message.length === 0) {
      errors.pushObject('messageEmpty');
    } else {
      errors.removeObject('messageEmpty');
    }

  }

  @computed(
    'currentThreadModel.assignment.sellerFeesCovered',
    'currentThreadModel.applications.@each.sellerFeesCovered',
    'currentUser.isSeller',
  )
  get sellerFeesCovered() {
    const { currentThreadModel } = this;

    if (!currentThreadModel?.assignment) {
      return false;
    }

    const firstApplication = currentThreadModel.applications?.firstObject;

    return firstApplication?.sellerFeesCovered ?? currentThreadModel.assignment.sellerFeesCovered;
  }

  /////////////////////////////////
  //  threadSearchResultsRaw/Model
  //
  // * The aggregate results list of the current thread search.
  // * Grows as users "get more" threads while scrolling through results
  /////////////////////////////////

  threadSearchResultsModel = undefined;
  threadSearchResultsRaw = undefined;


  @action
  watchThreadSearchResultsRaw() {
    let store = this.get('store');

    let allThreadsModel = this.get('allThreadsModel');
    let results = this.get('threadSearchResultsRaw');

    let model;

    if (typeof results !== 'undefined') {
      model = A();
      allThreadsModel.addObjects(results);

      results.forEach(thread => {
        model.pushObject(store.peekRecord('thread', thread.id));
      });
    }

    this.set('threadSearchResultsModel', model);
  }


  //*******************************
  /////////////////////////////////
  // Page Initialization
  /////////////////////////////////
  //*******************************

  @action
  initializeData() {
    this.inboxUnreadCount();
    this.archiveUnreadCount();
    this.watchRouteSegment();
    this.watchShowThreadBreakdown();
  }

  init() {
    super.init(...arguments);

    // Force observers/computed properties to fire for the following properties
    this.get('application');
    this.get('showThreadBreakdown');
    this.get('currentThreadErrorInfo');
    this.get('routeSegment');
    this.get('routeSegment.threadId');
    this.get('routeSegment.view');
    this.get('inboxThreadsModel');
    this.get('archivedThreadsModel');
    this.get('mostRecentInboxThreadsGrossModel');
    this.get('mostRecentArchiveThreadsGrossModel');

    // Initialize threadsCurrentlySendingMessages
    this.set('threadsCurrentlySendingMessages', new Set());

    // Bind the 'this' context to necessary internal functions
    ['_getMoreThreads',
      '_shouldIPullThreads',
      '_setThreadModelToRouteId',
      '_getNewMessages',
      '_initializeThreadStatuses',
      '_threadsQuery',
      '_setupDOM',
      '_teardownDOM',
      '_setIsMobileViewport',
      '_DOM_initializeObserverFunctionality',
      '_DOM_resizePanels',
      '_DOM_setupScrollables',
      '_DOM_afterTransitionSetup',
      '_DOM_closeDrawer',
      '_DOM_scrollToLastMessage',
      '_DOM_onShowThreadBreakdownChange',
      '_DOM_onShowBeginConversationChange',
      '_DOM_onThreadListModelChange',
    ].forEach(funcName => {
      this.set(funcName, this.get(funcName).bind(this));
    });

    this.set('allThreadsModel', A());
    // this.set('colleaguesList', Ember.Object.create());


    // Configure all deferred listeners on the body element.  We declare the functions
    // separately in init, so both the $.on() and $.off() can be written at the same
    // time -- doing this simplifies retrieval of the handlers, which must be named
    // and cannot be anonymous

    /////////////////////////////
    // Make several size checks, calculations, and adjustments every time the window is resized
    /////////////////////////////

    const windowOnResize = () => {
      this._DOM_closeDrawer();
      this._DOM_scrollToLastMessage();
      scheduleOnce('afterRender', this, this._DOM_resizePanels);
    };

    this.set('_DOM_attachListeners', () => {
      // Re-adjust view/panel calcuations when window is resized
      window.addEventListener('resize', windowOnResize, { passive: true });
    });

    this.set('_DOM_removeListeners', () => {
      window.removeEventListener('resize', windowOnResize);
    });
  }

  _setIsMobileViewport(e) {
    this.set('isMobileViewport', e.matches);
  }

  _pageSetup() {
    this.set('isLoadingRequiredResources', true);

    this.set('isLoadingThreads.inbox', true);
    this.set('threadsRequiredComplete.inbox', false);

    this.set('isLoadingThreads.archive', true);
    this.set('threadsRequiredComplete.archive', false);

    let store = this.get('store');
    let sortingUtils = this.get('sortingUtils');

    /////////////////////////////
    // Handle Specified Thread Incoming Links
    /////////////////////////////

    let directThread = this.get('routeSegment.threadId');

    if (directThread) {
      this.set('isLoadingRequiredResources', false);
      this.set('threadsRequiredComplete.inbox', true);
      this.set('threadsRequiredComplete.archive', true);
    }

    let promisesArray = A();

    /////////////////////////////
    // Get Threads
    /////////////////////////////

    let threadPromises = A(['inbox', 'archive']);

    threadPromises.forEach((item, index) => {

      let threadTypeIsCurrentView = (this.get('routeSegment.view') === item);

      // Configure setter/getter strings for different meta storage properties
      let lastThreadPageString = `lastThreadPage.${item}`;
      let maxThreadPageString = `maxThreadPages.${item}`;
      let threadSnapshotDateString = `threadSnapshotDates.${item}`;
      let isLoadingThreadsString = `isLoadingThreads.${item}`;
      let threadsRequiredCompleteString = `threadsRequiredComplete.${item}`;

      // Initialize the isLoadingState
      this.set(isLoadingThreadsString, true);

      // Initialize the 'snapshot' date to right before the threads are
      // pulled.  Since we only poll for inbox threads, we only set the
      // pollingThread snapshot date before pulling Inbox threads.

      let threadSnapshotDate = new Date();

      if (item === 'inbox') {
        this.set('threadSnapshotDates.pollingThreads', threadSnapshotDate);
        this.set('threadSnapshotDates.pollingMessages', threadSnapshotDate);
      }

      this.set(threadSnapshotDateString, threadSnapshotDate);

      // Perform the query

      let archived = index;
      let updatedAt = `${threadSnapshotDate.toISOString()}|<`;
      let page = 1;

      this._threadsQuery(archived, updatedAt, page).then(threads => {

        // Initialize the pseudo properties on the thread... (unread, watchedBy, archivedAt, etc.)

        let threadsArray = sortingUtils.forceArray(this._initializeThreadStatuses(threads));

        // In the case of empty initial conditions, 'allThreadsModel'
        // won't change because there will be no objects to add.  In order
        // to force appropriate page loading mechanisms to fire, we need
        // to explicitly set 'allThreadsModel'

        let allThreadsModel = this.get('allThreadsModel').slice();
        allThreadsModel.addObjects(threadsArray);
        this.set('allThreadsModel', allThreadsModel);

        // Set threadsRequiredComplete = true for the particular type
        this.set(threadsRequiredCompleteString, true);

        // If the current view matches the type of threads being retrieved,
        // and we've got threads to work with, we can safely show the
        // page to the user without waiting for the contacts check.  If our
        // response is empty however, we need to wait for the contacts
        // check to complete so we know which experience to show the
        // user (either an empty Inbox with Create Button or a message
        // instructing the user to post Assignments/Create Pitches)

        let cond1 = (threadTypeIsCurrentView && threads.get('length'));
        let cond2 = (this.get('threadsRequiredComplete.inbox') && this.get('threadsRequiredComplete.archive'));

        if (cond1 || cond2) {
          this.set('isLoadingRequiredResources', false);
        }

        // Inspect the meta from the payload object and determine how many
        // additional threads to load.

        let maxThreadPages = parseInt(threads.get('meta.pagination.total_pages'));

        this.set(maxThreadPageString, maxThreadPages);

        if (maxThreadPages < 2) {
          this.set(lastThreadPageString, 1);
          this.set(isLoadingThreadsString, false);
          return;
        }

        // Load the additional pages in the background

        let backgroundThreads = A();

        let pages = maxThreadPages < 3 ? maxThreadPages : 3;

        for (let i = 2; i <= pages; i++) {
          backgroundThreads.pushObject(this._threadsQuery(archived, updatedAt, i)).then(backgroundThreadsResult => {

            // Initialize the pseudo properties on the thread... (unread, watchedBy, archivedAt, etc.)

            let backgroundThreadsArray = sortingUtils.forceArray(this._initializeThreadStatuses(backgroundThreadsResult));

            this.get('allThreadsModel').addObjects(backgroundThreadsArray);
          });
        }

        return allSettled(backgroundThreads).then(responseArray => {
          responseArray.forEach((response) => {
            if (response.state === 'fulfilled') {
              // do something with success

            } else {
              // do something with an error
            }
          });

          this.set(lastThreadPageString, pages);
          this.set(isLoadingThreadsString, false);
        });
      });
    });

    promisesArray.pushObjects(threadPromises);


    /////////////////////////////
    // Get Contacts
    /////////////////////////////

    // There's no need to make this call if we've satisfied this check through other means
    if (this.get('isLoadingRequiredResources')) {
      // Check to make sure the threads calls have completed
      if (this.get('threadsRequiredComplete.inbox') && this.get('threadsRequiredComplete.archive')) {
        this.set('isLoadingRequiredResources', false);
      }
    }

    /////////////////////////////
    // Get Colleagues
    /////////////////////////////

    if (this.get('currentUser.company.id')) {

      this.set('isLoadingColleaguesList', true);
      this.set('colleaguesListRequiredComplete', false);

      let colleaguesPromise = store.query('user', {
        filter: {
          company_id: this.get('currentUser.company.id'),
        },
        page: 1,
        count: 500
      }).then((colleagues) => {
        const currentUserId = this.get('currentUser.id');

        const filteredColleagues = colleagues.filter((user) => {
          return user.get('id') !== currentUserId;
        });

        this.set('colleaguesList', filteredColleagues);
        this.set('colleaguesListRequiredComplete', true);
        this.set('isLoadingColleaguesList', false);
      });

      promisesArray.pushObject(colleaguesPromise);
    }

    all(promisesArray).then(() => {
      // Now that all the threads are loaded, we can begin loading user connections
      // and determine whether they should be loaded in the background or waited on.

    });
  }

  /**
   * The seller in this thread or null.
   *
   * @type {User|null}
   */
  @computed('currentThreadModel.etc.relationships.users')
  get _seller() {
    let usersMeta = this.get('currentThreadModel.etc.relationships.users');
    if (usersMeta) {
      let sellerMeta = usersMeta.findBy('role', 'seller');
      if (!sellerMeta) {
        return null;
      }

      return this.get('store').peekRecord('user', String(sellerMeta.user_id));
    }

    return null;
  }

  /**
   * The buyer in the thread or null.
   *
   * @type {User|null}
   */
  @computed('currentThreadModel.etc.relationships.users')
  get _buyer() {
    let usersMeta = this.get('currentThreadModel.etc.relationships.users');
    if (usersMeta) {
      let buyerMeta = usersMeta.findBy('role', 'buyer');
      if (!buyerMeta) {
        return null;
      }

      return this.get('store').peekRecord('user', String(buyerMeta.user_id));
    }
  }

  /**
   * @type {Boolean}    True if the buyer in the thread is blocked.  Can only return true if this is from the seller's perspective.
   */
  @computed('_buyer')
  get _isBuyerBlocked() {
    if (this.get('currentUser.isBuyer')) {
      return false;
    }

    let buyer = this.get('_buyer');
    if (!buyer) {
      return false;
    }

    return buyer.get('isBlockedBy');
  }

  /**
   * @type {Boolean}    True if the currentUser is a buyer AND the seller is blocked by the company.
   */
  @computed('currentUser.company.blockedUsers', '_seller')
  get _isSellerBlocked() {
    if (this.get('currentUser.isAdmin') || this.get('currentUser.isSeller') || !this.get('_seller')) {
      return false;
    }

    let company = this.get('store').peekRecord('company', this.get('currentUser.company.id'));
    if (!company) {
      this.get('errorTracker').error(
        `_isSellerBlocked -- company not found for user id ${this.get('currentUser.id')}`,
        this);
      return false;
    }

    return company.isUserBlockedByCompany(this.get('_seller'));
  }

  /**
   * @type {Boolean}    True if the message is blocked.  Could be from either the buyer or seller's persepctives.
   */
  @computed('_isSellerBlocked', '_isBuyerBlocked')
  get _isMessageBlocked() {
    return this.get('_isSellerBlocked') || this.get('_isBuyerBlocked');
  }

  /**
   * @type {Boolean} Whether the save button is disabled.
   */
  @computed(
    'isUploadingAssets',
    'currentThreadErrorInfo.errors.[]',
  )
  get _isSaveButtonDisabled() {
    return this.get('isUploadingAssets')
      || this.get('currentThreadErrorInfo.errors.length');
  }

  /**
   * Watches the upload list and sets the hasUploads flag accordingly.
   */
  @action
  observeUploadList() {
    const isInCreateThreadMode = false;
    const hasUploads = this.get('uploadList.length') > 0;

    if (isInCreateThreadMode) {
      this.set('mcStates.hasUploadsInCreateThreadMode', hasUploads);

      // Reset the other for safety.
      this.set('mcStates.hasUploadsInNormalMode', false);
      return;
    }

    this.set('mcStates.hasUploadsInNormalMode', hasUploads);

    // Reset the other for safety.
    this.set('mcStates.hasUploadsInCreateThreadMode', false);
  }

  /**
   * Adds a message to the specified thread.
   */
  _sendMessage(threadID) {
    const currentUser = this.currentUser;

    const jsonData = {
      user_id: currentUser.id,
      body: this.currentComposedMessageModel,
    };

    if (threadID) {
      jsonData.thread_id = threadID;
    }

    const related_asset_ids = this.uploadCompletedList.map((uploadID) => {
      return this.get(`uploads.${uploadID}.assetID`);
    });

    if (related_asset_ids.length) {
      jsonData.related_asset_ids = related_asset_ids;
    }

    const includes = [
      'assets.uri-collection',
      'user',
      'actions',
    ];

    const options = this.shData.addIncludesToOptions({ jsonData }, includes);

    return this.api.post('messages/', options);
  }

  /** @type {User} */
  profileOverlayUser = null;

  closeProfileOverlay() {
    this.set('application.profileOverlay', null);
  }

  @action
  launchModalDeclineOnArchive(pendingProjects) {
    const currentThread = this.currentThreadModel;

    this.set('modals.declineOnArchive.currentThread', currentThread);
    this.set('modals.declineOnArchive.pendingProjects', pendingProjects);

    this.set('modals.declineOnArchive.afterSuccessfulDeclineCallback', () => {

      const threadManager = this.threadManager;
      const currentUser = this.currentUser;

      threadManager.actionMarkThreadRead(currentThread, currentUser);

      return allSettled([this._getNewMessages(false), this.archiveConversation()]);
    });

    showModal(this, 'message-center/decline-on-archive', {
      currentThread: this.modals.declineOnArchive.currentThread,
      pendingProjects: this.modals.declineOnArchive.pendingProjects,
      afterSuccessfulDeclineCallback: this.modals.declineOnArchive.afterSuccessfulDeclineCallback,
      onModalClose: this.closeModalDeclineOnArchive,
      stateServiceName: 'states/message-center',
      stateServicePath: 'modals.declineOnArchive',
      stateServiceUniqueBy: 'currentThread.id',
    });
  }

  @action
  archiveConversation() {
    const currentUser = this.currentUser;
    const currentThread = this.currentThreadModel;
    const mcStates = this.mcStates;

    // Update awaiting response status
    mcStates.set('threadBreakdown.archiveConversationAwaitingResponse', true);

    return currentThread.archive(this.CEW.format({id: currentUser.id, type: 'user'})).then(() => {
      this.threadManager.archiveThreadAndSyncMeta(currentThread, currentUser);
      this._addThreadToArchivedModel(currentThread);

      this.set('currentThreadModel', undefined);
    }).finally(() => {
      // Reset awaiting response status
      mcStates.set('threadBreakdown.archiveConversationAwaitingResponse', false);
    });
  }

  @action
  beginConversation() {
    const currentUser = this.currentUser;

    const newThread = this.store.createRecord('thread', {
      users: [currentUser],
      unread: false,
      createdAt: new Date(),
      recipient: undefined,
    });


    this.set('newThreadModel', newThread);
    this.setCurrentComposedMessageModel('');
    this.resetAssetUploadManager();

    const resetSelectedRecipient = this.resetSelectedRecipient;
    if (typeof resetSelectedRecipient === 'function') {
      resetSelectedRecipient();
    }

    this.set('showBeginConversation', true);
    this.set('QPCreateThread', true);
  }

  @action
  selectRecipient(selectedRecipient) {
    const newRecipient = this.store.peekRecord('user', selectedRecipient.id);
    const newThreadModel = this.newThreadModel;

    newThreadModel.set('users', [this.currentUser, newRecipient]);
    newThreadModel.set('recipient', newRecipient);
  }

  @action
  markConversationUnread() {
    let currentThread = this.get('currentThreadModel');
    let currentUser = this.get('currentUser');
    let CEW = this.get('CEW');
    let threadManager = this.get('threadManager');
    let mcStates = this.get('mcStates');

    mcStates.set('threadBreakdown.markUnreadAwaitingResponse', true);

    currentThread.markUnread(CEW.format({id: this.get('currentUser.id'), type: 'user'})).then(updatedThread => {

      // temporary change
      threadManager.markThreadUnreadAndSyncMeta(currentThread, currentUser);

      // force thread lists to re-calculate
      this.get('allThreadsModel').addObject(currentThread);

      this.set('currentThreadModel', undefined);
    }).finally(() => {
      // update awaiting response status
      mcStates.set('threadBreakdown.markUnreadAwaitingResponse', false);
    });
  }

  @action
  checkForPendingProjects() {
    let currentThread = this.get('currentThreadModel');
    const currentUser = this.get('currentUser');

    let projects = currentThread.get('applications');

    if (currentUser.get('isBuyer') && projects && projects.length) {
      let pendingProjects = A();

      projects.forEach(project => {
        if (project.get('isPending')) {
          pendingProjects.pushObject(project);
        }
      });

      if (pendingProjects.get('length') > 0) {
        this.launchModalDeclineOnArchive(pendingProjects);
        return;
      }
    }

    this.archiveConversation();
  }

  @action
  launchModalManageWatchers() {
    let currentThread = this.get('currentThreadModel');
    this.set('modals.manageWatchers.threadToWatch', currentThread);
    this.set('modals.manageWatchers.colleaguesList', this.get('colleaguesList'));
    showModal(this, 'message-center/manage-watchers', {
      threadToWatch: this.modals.manageWatchers.threadToWatch,
      colleaguesList: this.modals.manageWatchers.colleaguesList,
    });
  }

  @action
  openProjectsDrawer() {
    this.set('drawerOpen', true);
  }

  @action
  handleProfileOverlayClose() {
    this.closeProfileOverlay();
  }

  /**
   * @param {Ember.Component} profileOverlay
   * @param {User} user
   */
  @action
  handleProfileLinkClick(profileOverlay, user) {
    this.set('profileOverlayUser', user);
    this.set('application.profileOverlay', profileOverlay);
  }

  /**
   * Called when the file input component receives new files.
   */
  @action
  onFileInputChange(files) {
    this.addFiles(files);
  }

  /**
   * Called when an asset's delete button is clicked.
   */
  @action
  onClickDeleteAssetButton(uploadID) {
    this.removeFile(uploadID);
  }

  /**
   * Prevents the blur event from firing on the textarea
   *
   * @param {MouseEvent} e
   */
  @action
  preventBlurOnMouseDown(e) {
    e.preventDefault();
  }

  @action
  showSavedMessagesModal(e) {
    e.preventDefault();

    showModal(this, 'saved-messages', {
      onMessageSelect: (message) => {
        closeModal(this);

        const currentValue = this.get('currentComposedMessageModel') || '';
        const newValue = currentValue + (currentValue ? '\n' : '') + message.get('body');

        this.setCurrentComposedMessageModel(newValue);

        scheduleOnce('afterRender', this, () => {
          this.composerElement?.focus();
        });
      },
    });
  }

  /**
   * Called when the user clicks the freelancer earnings calculator action
   *
   * @param {MouseEvent} e
   */
  @action
  showFreelancerEarningsCalculatorModal(threadModel, buyer, seller, sellerFeesCovered, e) {
    e.preventDefault();

    const modalModel = {
      threadModel,
      buyer,
      seller,
      isToggleView: false,
      sellerFeesCovered,
    };

    showModal(this, 'freelancer-earnings-calculator', modalModel);
  }

  // Searching for Threads

  /////////////////////////////////
  //  clearSearchResults
  //
  // * Clears the thread search results
  /////////////////////////////////

  @action
  clearSearchResults() {

  }

  /////////////////////////////////
  //  getMoreSearchThreads
  //
  // * Retrieves the next page of threads (from the server) in the current thread search
  /////////////////////////////////
  @action
  getMoreSearchThreads() {
    let store = this.get('store');

    let lastPage = this.get('lastThreadPage.search');
    let currentPage = lastPage + 1;

    this.set('isLoadingGetMoreSearchThreadsResults', true);

    store.query('thread', {
      filter: {
        user_id: this.get('currentUser.id')
      },
      search: 1,
      include: 'messages.assets.uri-collection,users,users.company,all-time-users,assignment',
      count: SEARCH_THREAD_PAGE_SIZE,
      page: currentPage
    }).then((returnedThreads) => {

      returnedThreads = this.get('sortingUtils').forceArray(returnedThreads);

      let allThreadsModel = this.get('allThreadsModel');
      let threadSearchResultsModel = this.get('threadSearchResultsModel');

      allThreadsModel.addObjects(returnedThreads);
      threadSearchResultsModel.addObjects(returnedThreads);

      this.set('isLoadingGetMoreSearchThreadsResults', false);
      this.set('lastThreadPage.search', currentPage);
    });
  }

  // Thread list

  /////////////////////////////////
  //  openThread
  //
  // * Opens a thread for viewing
  /////////////////////////////////

  @action
  openThread(thread) {
    this.set('currentThreadModel', thread);
  }


  // Thread Breakdown


  /////////////////////////////////
  //  sendComposedMessage
  //
  // * Sends a new message in the existing thread
  /////////////////////////////////
  @action
  sendComposedMessage(event) {
    event.preventDefault();
    event.stopPropagation();

    const currentUser = this.get('currentUser');
    let currentThread = this.get('currentThreadModel');

    /////////////////////////////////
    // Form Validation!
    /////////////////////////////////

    let currentThreadErrorInfo = this.get('currentThreadErrorInfo');

    // First check for hard errors
    if (currentThreadErrorInfo.get('errors.length')) {
      currentThreadErrorInfo.set('showWarning', true);

      let warning = currentThreadErrorInfo.get('errorMessages').get(currentThreadErrorInfo.get('errors.firstObject'));

      currentThreadErrorInfo.set('warning', warning);
      return;
    }

    // We can now set 'showWarning' to false and safely proceed
    currentThreadErrorInfo.set('showWarning', false);

    this.get('threadsCurrentlySendingMessages').add(currentThread.id);
    $(event.target).parents('.send-button-container').addClass('send-in-progress');

    this._sendMessage(currentThread.id)
      .then((response) => {
        this.shData.pushResponseToStore(response);

        const message = this.get('store').peekRecord('message', response.data.id);
        message.set('thread', currentThread);

        scheduleOnce('afterRender', this, function () {
          this._DOM_scrollToLastMessage();
        });

        // Update character counters and height resizing (mobile) on textarea
        this.setCurrentComposedMessageModel('');

        // Clear out assets.
        this.resetAssetUploadManager();
      })
      .then(() => {
        return this.get('threadManager').actionMarkThreadRead(currentThread, currentUser);
      })
      .catch((error) => {
        this.get('errorTracker').error('sendComposedMessage - failed', this, error);
      })
      .finally(() => {
        this.get('threadsCurrentlySendingMessages').delete(currentThread.id);
        $(event.target).parents('.send-button-container').removeClass('send-in-progress');
      });
  }

  /////////////////////////////////
  //  unarchiveConversation
  //
  // * Restores the current thread to the Inbox
  /////////////////////////////////
  @action
  unarchiveConversation(event) {
    event.preventDefault();
    event.stopPropagation();

    let currentUser = this.get('currentUser');
    let currentThread = this.get('currentThreadModel');
    let CEW = this.get('CEW');
    let threadManager = this.get('threadManager');
    let mcStates = this.get('mcStates');

    // Update awaiting response status
    mcStates.set('threadBreakdown.restoreConversationAwaitingResponse', true);

    currentThread.unarchive(CEW.format({id: this.get('currentUser.id'), type: 'user'})).then((updatedThread) => {
      threadManager.unarchiveThreadAndSyncMeta(currentThread, currentUser);
      this._addThreadToInboxModel(currentThread);

      this.set('currentThreadModel', undefined);
    }).finally(() => {
      // Reset awaiting response status
      mcStates.set('threadBreakdown.restoreConversationAwaitingResponse', false);
    });
  }

  // Thread Breakdown - Mobile

  /////////////////////////////////
  //  backToThreadList
  //
  // * Takes the user back to the thread list when pressing the back button on mobile
  /////////////////////////////////
  @action
  backToThreadList(event) {

    event.preventDefault();
    event.stopPropagation();

    if (this.get('showInboxView')) {
      this.get('router').transitionTo('message-center.inbox.index');
    } else {
      this.get('router').transitionTo('message-center.archive.index');
    }
  }

  // Modals

  /////////////////////////////////
  //  closeModalDeclineOnArchive
  //
  // * Removes the modal from the template (called on modal close)
  /////////////////////////////////
  @action
  closeModalDeclineOnArchive() {
    this.set('modals.declineOnArchive.currentThread', undefined);
    closeModal(this);
  }

  // Component/Misc

  /////////////////////////////////
  //  getNewMessages
  //
  // * Convenience wrapper to enable passing as an action.  (Used by the Project List component)
  /////////////////////////////////
  @action
  getNewMessages(isPolling, thread) {
    return this._getNewMessages(isPolling, thread);
  }

  //*******************************
  /////////////////////////////////
  // Internal Utility Functions
  /////////////////////////////////
  //*******************************


  /////////////////////////////////
  //  _initializeThreadStatuses
  //
  // * Initializes pseudo properties on the threads based on meta information
  /////////////////////////////////

  _initializeThreadStatuses(threads) {
    threads = this.get('sortingUtils').forceArray(threads);
    let currentUser = this.get('currentUser');
    let threadManager = this.get('threadManager');

    threads.forEach((thread) => {
      threadManager.initializeThreadPseudoProperties(thread, currentUser);
    });

    if (threads.get('length') === 1) {
      return threads[0];
    } else {
      return threads;
    }
  }

  /////////////////////////////////
  //  _addThreadToArchivedModel
  //
  // * Adds the thread to the archivedThreadsModel and removes it from the inboxThreadsModel
  /////////////////////////////////

  _addThreadToArchivedModel(thread) {
    this.get('inboxThreadsModel').removeObject(thread);
    this.get('archivedThreadsModel').pushObject(thread);
  }

  /////////////////////////////////
  //  _addThreadToInboxModel
  //
  // * Adds the thread to the inboxThreadsModel and removes it from the archivedThreadsModel
  /////////////////////////////////

  _addThreadToInboxModel(thread) {
    this.get('inboxThreadsModel').pushObject(thread);
    this.get('archivedThreadsModel').removeObject(thread);
  }

  _markThreadRead({ threadID, timestamp }) {
    // If the thread is not specified, assume the current thread.
    const id = threadID || this.get('currentThreadModel.id');

    if (!id) return;

    const thread = this.get('store').peekRecord('thread', id);

    if (!thread) return;

    const user = this.get('currentUser');

    let messageID;

    /*
     * If a timestamp was provided, we want to mark the given thread as read only up to the that
     * point. The mark read endpoint accepts a message ID, not a timestamp, so we need to find the
     * most recent message that was sent before the given timestamp.
     */
    if (timestamp) {
      // Find the most recent message before the given timestamp.
      const message = thread.get('messages').find((message) => {
        return message.get('createdAt').getTime() < timestamp.getTime();
      });

      messageID = message ? message.get('id') : null;
    }

    return this.get('threadManager').actionMarkThreadRead(thread, user, messageID);
  }

  /////////////////////////////////
  //  _setThreadModelToRouteId
  //
  // * Takes the thread id from the routeSegment and uses it
  // * to locate the thread in the store.  Once found, intializes
  // * the thread's pseudo properties, adds it to the allThreadsModel,
  // * and sets the currentThreadModel equal to the corresponding Thread
  /////////////////////////////////

  _setThreadModelToRouteId() {
    let store = this.get('store');
    let threadId = this.get('routeSegment.threadId');
    let allThreadsModel = this.get('allThreadsModel');

    let thread = store.peekRecord('thread', threadId);

    if (typeof thread.get('unread') === 'undefined') {
      this._initializeThreadStatuses(thread);
    }

    if (!allThreadsModel.includes(thread)) {
      allThreadsModel.addObject(thread);
    }

    if (this.get('currentThreadModel.id') !== thread.get('id')) {
      this.set('currentThreadModel', thread);
    }
  }

  /////////////////////////////////
  //  _sortThreadList
  //
  // * Sorts the thread list and returns a sorted copy
  /////////////////////////////////


  _sortThreadList(model) {
    model = (!Array.isArray(model)) ? model.toArray() : model.slice();

    model.sort(function (a, b) {
      if (a.get('updatedAt') > b.get('updatedAt')) {
        return -1;
      } else if (a.get('updatedAt') < b.get('updatedAt')) {
        return 1;
      } else {
        let aId = parseInt(a.get('id'));
        let bId = parseInt(b.get('id'));
        if (aId > bId) {
          return -1;
        } else if (aId < bId) {
          return 1;
        } else {
          return 0;
        }
      }
    });

    return model;
  }

  /////////////////////////////////
  //  _shouldIPullThreads
  //
  // * Logic for determining whether to pull new threads during "infinite scroll"
  // * of the current thread list
  /////////////////////////////////

  _shouldIPullThreads() {

    let showInboxView = this.get('showInboxView');
    let showThreadSearchResults = this.get('showThreadSearchResults');

    let currentView = showInboxView ? 'inbox' : 'archive';
    let isLoadingThreads = this.get(`isLoadingThreads.${currentView}`);

    if (showThreadSearchResults) {
      return false;
    }

    let maxThreadPages, lastThreadPage;

    if (showInboxView) {
      maxThreadPages = this.get('maxThreadPages.inbox');
      lastThreadPage = this.get('lastThreadPage.inbox');
    } else {
      maxThreadPages = this.get('maxThreadPages.archive');
      lastThreadPage = this.get('lastThreadPage.archive');
    }

    if (isLoadingThreads || lastThreadPage === maxThreadPages) {
      return false;
    } else {
      return true;
    }
  }

  /////////////////////////////////
  //  _getMoreThreads
  //
  // * Gets another page of threads from the server
  /////////////////////////////////

  _getMoreThreads() {
    let sortingUtils = this.get('sortingUtils');
    let showInboxView = this.get('showInboxView');

    let currentViewString = showInboxView ? 'inbox' : 'archive';

    let lastThreadPageString = `lastThreadPage.${currentViewString}`;
    let updatedAtString = `threadSnapshotDates.${currentViewString}`;
    let isLoadingThreadsString = `isLoadingThreads.${currentViewString}`;

    let lastThreadPage = this.get(lastThreadPageString);
    let updatedAt = `${this.get(updatedAtString).toISOString()}|<`;

    let newThreadPage = lastThreadPage + 1;
    this.set(isLoadingThreadsString, true);

    let archived = showInboxView ? 0 : 1;

    let threadsPromise = this._threadsQuery(archived, updatedAt, newThreadPage);

    threadsPromise.then(threads => {
      let threadsArray = sortingUtils.forceArray(threads);
      this.get('allThreadsModel').addObjects(threadsArray);
      this.set(lastThreadPageString, newThreadPage);
      this.set(isLoadingThreadsString, false);

      return threads;
    });
  }

  @action
  watchMostRecentInboxThreadsGrossModel() {
    let polledThreads = this.get('mostRecentInboxThreadsGrossModel');
    this.set('polledThreadsModel', polledThreads);
  }

  @action
  watchMostRecentArchiveThreadsGrossModel() {
    let polledThreads = this.get('mostRecentArchiveThreadsGrossModel');
    this.set('polledThreadsModel', polledThreads);
  }

  /////////////////////////////////
  //  _getNewMessages
  //
  // * Gets new messages for a particular thread
  /////////////////////////////////

  _getNewMessages(isPolling, thread) {
    let currentThreadModel = (typeof thread !== 'undefined') ? thread : this.get('currentThreadModel');
    let store = this.get('store');

    let filter = {
      user_id: this.get('currentUser.id'),
      id: currentThreadModel.get('id')
    };

    if (isPolling) {
      let lastSnapshot = this.get('threadSnapshotDates.pollingMessages');

      if (!lastSnapshot) {
        return;
      }

      // filter.updated_at = `${lastSnapshot.toISOString()}|>`

      let currentSnapshot = new Date();
      this.set('threadSnapshotDates.pollingMessages', currentSnapshot);
    }

    return store.query('thread', {
      filter: filter,
      page: 1,
      count: 1,
      include: 'messages,messages.actions,users,users.company,all-time-users,applications,assignment'
    }).then(returnedThread => {
      // Do something to announce new messages perhaps
    });
  }

  /////////////////////////////////
  //  _threadsQuery
  //
  // * Generates the appropriate query method for store.query
  // * and returns an un-"then"-ed promise
  /////////////////////////////////

  _threadsQuery(archived, updatedAt, page) {
    page = (typeof page !== 'undefined') ? page : 1;
    archived = (typeof archived !== 'undefined') ? archived : 0;

    const include = 'messages.actions,messages.assets.uri-collection,users,users.company,all-time-users,assignment';
    const count = STANDARD_THREAD_PAGE_SIZE;

    // Build the filter

    const filter = {
      user_id: this.currentUser.id,
      archived: {
        user_id: this.currentUser.id,
        value: archived,
      }
    };

    if (updatedAt) {
      filter.updatedAt = updatedAt;
    }

    // Return the query

    return this.store.query('thread', {
      filter,
      page,
      count,
      include,
    });
  }

  /////////////////////////////////
  //  searchThreadsAPI Function
  //
  // * Defines the function call to perform server-side thread search.
  // * Passed to sh-api-search component
  /////////////////////////////////

  @computed('')
  get searchThreadsAPIFunction() {
    return function (searchValue) {
      let store = this.get('store');

      let currentQuery = searchValue.toString().trim();
      this.set('threadSearchLastQuery', currentQuery);

      let searchQuery = store.query('thread', {
        filter: {
          user_id: this.get('currentUser.id'),
          archived: {
            user_id: this.get('currentUser.id'),
            value: 0
          }
        },
        search: 1,
        'search-term': currentQuery,
        count: SEARCH_THREAD_PAGE_SIZE,
        include: 'messages.assets.uri-collection,users,users.company,all-time-users,assignment'
      }).then(searchResults => {

        let maxPages = parseInt(searchResults.get('meta.pagination.total_pages'));
        let totalResults = parseInt(searchResults.get('meta.pagination.total'));

        let resultsArray = this.get('sortingUtils').forceArray(searchResults);

        this.set('maxThreadPages.search', maxPages);
        this.set('lastThreadPage.search', 1);
        this.set('totalThreadResults.search', totalResults);

        return resultsArray;
      });

      return searchQuery;

    }.bind(this);
  }

  //*******************************
  /////////////////////////////////
  // DOM Setup/Manipulation
  /////////////////////////////////
  //*******************************


  /////////////////////////////////
  //  _setupDOM
  //
  // * initializes the DOM
  /////////////////////////////////

  _setupDOM() {
    this.mqList = window.matchMedia('(max-width: 640px)');
    this.mqList.addEventListener('change', this._setIsMobileViewport);

    this._setIsMobileViewport(this.mqList);

    scheduleOnce('afterRender', this, function () {
      this._DOM_afterTransitionSetup();

      this._DOM_attachListeners();

      // Only initialize Observer functionality if the page has already been loaded
      if(this.get('pageSetupComplete')) {
        this._DOM_initializeObserverFunctionality();
      }
    });
  }

  /////////////////////////////////
  //  _teardownDOM
  //
  // * tears down classes + listeners the DOM
  /////////////////////////////////

  _teardownDOM() {
    this.mqList?.removeEventListener('change', this._setIsMobileViewport);
    this.mqList = null;

    this._DOM_removeListeners();
  }

  /////////////////////////////////
  //  _DOM_afterTransitionSetup
  //
  // * Performs all necessary intialization actions after transitioning between
  // * nested routes within the Message Center (/messages)
  /////////////////////////////////

  _DOM_afterTransitionSetup() {
    scheduleOnce('afterRender', this, this._DOM_resizePanels);
    this._DOM_setupScrollables();
  }

  /////////////////////////////////
  //  _DOM_resizePanels
  //
  // * Calculates and sets the new height for each of the panels/columns in Message Center layout
  /////////////////////////////////

  _DOM_resizePanels() {
    let headerHeight = $('header.js-app-header').outerHeight();
    let subNavHeight = $('main .sub-navigation').outerHeight();
    let msgCenterGapHeight = 0;
    let panelContainerGapHeight = 0;

    $.each($('main .message-center-body').css(['margin-top', 'margin-bottom', 'padding-top', 'padding-bottom']), function (index, val) {
      msgCenterGapHeight += parseInt(val);
    });

    $.each($('main .panel-container').css(['margin-top', 'margin-bottom', 'padding-top', 'padding-bottom']), function (index, val) {
      panelContainerGapHeight += parseInt(val);
    });

    let panelHeight = $(window).height() - headerHeight - subNavHeight - msgCenterGapHeight - panelContainerGapHeight;

    if ($(window).prop('innerWidth') <= 640) {
      $('.panel, .panel-container, .message-center-body').css('height', '');

    } else {
      $('.panel').css('height', panelHeight);
      $('.panel-container').css('height', '');
      $('.message-center-body').css('height', panelHeight + msgCenterGapHeight + panelContainerGapHeight + 1);
      $('main').first().css('height', null);
    }
  }

  /////////////////////////////////
  //  _DOM_setupScrollables
  //
  // * Initializes the "infinite scroll" behavior of the thread list
  /////////////////////////////////

  _DOM_setupScrollables() {

    scheduleOnce('afterRender', this, function () {
      // We need to remove any scroll events on the thread-list
      // before adding any to avoid accidental duplication of
      // events while transitioning between routes.  This shouldn't
      // be practically necessary, but due to the fact that
      // Ember renders the "Loading" screen on individual thread
      // pages, any directly attached (non-deferred) events don't
      // get properly applied, and must be manually setup.  And
      // just in case you're wondering why these scroll events
      // aren't deferred, it's because browser quirks don't allow it.  Yay quirks!

      $('.panel-1 ul.thread-list').off('scroll');

      let self = this;

      $('.panel-1 ul.thread-list').on('scroll', function (e) {

        let list = $(this);
        let listItem = list.find('li').first();

        let listHeight = parseInt(list.css('height'));
        let listScrollHeight = parseInt(list.prop('scrollHeight'));
        let listScrollTop = list.scrollTop();

        let listItemHeight = parseInt(listItem.css('height'));

        let scrollCondition = (listScrollHeight - listHeight - listScrollTop < listItemHeight);
        let threadsCondtion = self._shouldIPullThreads();

        if (scrollCondition && threadsCondtion) {
          self._getMoreThreads();
        }
      });

      this.set('scrollablesSet', true);
    });
  }

  /////////////////////////////////
  //  _DOM_closeDrawer
  //
  // * Checks to see if the viewport is mobile and then closes the drawer if not
  /////////////////////////////////

  _DOM_closeDrawer() {
    if (!this.isMobileViewport) {
      this.closeDrawer();
    }
  }

  /////////////////////////////////
  //  _DOM_scrollToLastMessage
  //
  // * Scrolls the thread breakdown's messages list
  // * to the most recent message, taking the viewport
  // * size into account
  /////////////////////////////////

  _DOM_scrollToLastMessage() {
    let threadMessagesList = $('.thread-messages-list');

    if (this.get('isMobileViewport')) {
      threadMessagesList.scrollTop(threadMessagesList.first().prop('scrollHeight'));
    } else {
      threadMessagesList.scrollTop(0);
    }
  }

  /////////////////////////////////
  //  _DOM_initializeObserverFunctionality
  //
  // * Initializes any DOM behavior attached to Observers.  On init(), the observers
  // * should largely taker care of this, but when re-visting the Message Center
  // * from other Ember routes, we need to re-set any/all behaviors + classes that
  // * were otherwise lost on DOM rebuilds between routes.
  /////////////////////////////////

  _DOM_initializeObserverFunctionality() {
    this._DOM_onShowThreadBreakdownChange();
    this._DOM_onShowBeginConversationChange();
    this._DOM_onThreadListModelChange();
  }

  /////////////////////////////////
  //  _DOM_onShowThreadBreakdownChange
  //  _DOM_WatchShowThreadBreakdown
  //
  // * Updates body class for showThreadBreakdown and fires off
  // * a _DOM_scrollToLastMessage() call
  /////////////////////////////////

  _DOM_onShowThreadBreakdownChange() {
    let showThreadBreakdown = this.get('showThreadBreakdown');

    if (showThreadBreakdown) {
      scheduleOnce('afterRender', this, function () {
        this._DOM_scrollToLastMessage();
      });
    }
  }

  @action
  _DOM_WatchShowThreadBreakdown() {
    this._DOM_onShowThreadBreakdownChange();
  }

  /////////////////////////////////
  //  _DOM_onShowBeginConversationChange
  //  _DOM_WatchShowBeginConversation
  //
  // * Updates body class for showBeginConversation and resizes the panels
  /////////////////////////////////

  _DOM_onShowBeginConversationChange() {
    scheduleOnce('afterRender', this, this._DOM_resizePanels);
  }

  @action
  _DOM_WatchShowBeginConversation() {
    this._DOM_onShowBeginConversationChange();
  }

  /////////////////////////////////
  //  _DOM_onThreadListModelChange
  //  _DOM_WatchThreadListModel
  //
  // * Updates body and .three-panel-container class for an empty thread list , then resizes the panels
  /////////////////////////////////

  _DOM_onThreadListModelChange() {
    scheduleOnce('afterRender', this, this._DOM_resizePanels);
  }

  @action
  _DOM_WatchThreadListModel() {
    this._DOM_onThreadListModelChange();
  }
}

export default MessageCenterController;
